import { Nav } from '@accedo/spatial-nav';
import { KeyCodes, VirtualKeys } from '@accedo/virtual-key';
import { Image } from '@accedo/ctv-image';
import { FunctionalComponent } from 'preact';
import { useEffect, useState, useMemo } from 'preact/hooks';
import { stylesheet } from 'astroturf';
import clsx from 'clsx';
import KeyEventHelper from '##/utils/KeyEventHelper';
import {
  MAX_MARQUEE_CHAR_LENGTH,
  MARQUEE_BUTTON_NAV_ID,
  isHomeLiveCarouselId,
  GENRE_SELECTOR_NAV_ID,
  safeAssignFocus,
  SIDE_MENU_NAV_ID,
} from '##/utils';
import { useInterval, useReferredState } from '##/utils/hooks';
import { PageNavigation } from '##/utils/PageNavigation';
import { config, focusCache } from '##/services/store/store';
import { getBaseApiEndpoint } from '##/services/tenApi/apirequest';
import TealiumBridge from '##/services/analytics/Tealium/TealiumBridge';
import { nav } from '##/init';
import { MarqueeShowInfo } from './MarqueeShowInfo';
import { MarqueeData, MarqueeItem } from './types';

const styles = stylesheet`
  @import '##/styles/import.scss';

  .marquee-item-nav-container {
    margin-top: pxToVh(33);
    width: 100vw;
    display: flex;
  }

  .marquee-nav-indicator {
    height: pxToVh(13);
    width: pxToVh(13);
    background: $pure-white;
    opacity: 0.4;
    border-radius: 100%;
    margin-right: pxToVw(15.5);

    &.active-marquee-nav-indicator {
      opacity: 1;
    }
  }

  .marquee-content-container {
    width: 100vw;
    height: 70vh;
    padding-top: pxToVh(185);
    padding-left: pxToVw(125);
    margin-bottom: 8.2vh;
  }

  .marquee-long-title-content-container {
    @extend .marquee-content-container;

    padding-top: pxToVh(130);
  }

  .marquee-background-container {
    width: 100vw;
    height: 100vh;
    position: absolute;
    z-index: -1;
    top: 0;
  }

  .marquee-background-image {
    width: 100%
  }
`;

export const HomeMarquee: FunctionalComponent<MarqueeData> = (props) => {
  const [, currentItemRef, setCurrentMarqueeItemIndex] = useReferredState(0);
  const [hasFocus, setHasFocus] = useState(false);
  const { marqueeAutoScrollTime } = config.$get().app;
  const { id, items, isInbound } = props;

  const marqueeData = useMemo(() => {
    if (!items.length) {
      return [];
    }

    return items.map((item) => ({
      ...item,
      title: item?.title.trim() || item.title,
    }));
  }, [items.length]);

  const homeMarqueeKeyListenerId = 'homeMarqueeKeyListener';
  const fallbackUrl = '/assets/placeholders/Fallback-Marquee.jpg';

  const onFocusWithInChange = (entered: boolean): void => {
    setHasFocus(entered);
  };

  const onSelect = (item: MarqueeItem): void => {
    const { ctaEndpoint, apiEndpoint, ctaType, type } = item || {};
    const marqueeItemEndpoint = ctaEndpoint || apiEndpoint;
    const marqueeType = ctaType || type;
    const baseEndpoint = getBaseApiEndpoint(marqueeItemEndpoint);
    const endpointParts = baseEndpoint?.split('/') || [];
    const id = endpointParts[endpointParts.length - 1];

    TealiumBridge.onCarouselItemClick(item);

    switch (marqueeType?.toLowerCase()) {
      case 'show':
        PageNavigation.navigateTo(`/show/${id}`, marqueeItemEndpoint);
        break;
      case 'channel':
        PageNavigation.navigateTo(`/player/live/${id}`, item);

        break;
      case 'genre':
        focusCache.tvShowLanding.$set({ genreKey: marqueeItemEndpoint });

        PageNavigation.navigateTo(`/shows/${id}`);
        break;
      case 'videopost':
        PageNavigation.navigateTo(`/player/vod/${id}`, item);
        break;
      default:
        break;
    }
  };

  const onKeydown = (keyName: VirtualKeys): boolean => {
    if (keyName === KeyCodes.vKey.BACK) {
      safeAssignFocus(SIDE_MENU_NAV_ID);

      return true;
    }
    if (keyName === KeyCodes.vKey.LEFT) {
      if (currentItemRef.current !== 0) {
        setCurrentMarqueeItemIndex(currentItemRef.current - 1);
        return true;
      }
    } else if (keyName === KeyCodes.vKey.RIGHT) {
      setCurrentMarqueeItemIndex(
        (currentItemRef.current + 1) % marqueeData.length,
      );
    }
    return false;
  };

  useInterval(
    () => {
      if (
        isHomeLiveCarouselId(nav.currentFocusNodeId) ||
        nav.currentFocusNodeId.includes(GENRE_SELECTOR_NAV_ID)
      ) {
        return;
      }

      setCurrentMarqueeItemIndex(
        (currentItemRef.current + 1) % marqueeData.length,
      );
    },
    marqueeAutoScrollTime > 0 ? marqueeAutoScrollTime * 1000 : null,
  );

  useEffect(() => {
    if (hasFocus) {
      KeyEventHelper.addKeydownListener(onKeydown, homeMarqueeKeyListenerId);
      return () =>
        KeyEventHelper.removeKeydownListener(homeMarqueeKeyListenerId);
    }
    KeyEventHelper.removeKeydownListener(homeMarqueeKeyListenerId);
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return () => {};
  }, [hasFocus]);

  const currentMarqueeItem = marqueeData[currentItemRef.current];

  const isLongMarquee =
    currentMarqueeItem?.title?.length > MAX_MARQUEE_CHAR_LENGTH &&
    currentMarqueeItem.title.includes(' ');

  return (
    <Nav.Vertical id={id} onFocusWithinChange={onFocusWithInChange}>
      {currentMarqueeItem ? (
        <div className={styles.marqueeBackgroundContainer}>
          {isInbound && (
            <Image
              url={
                `${currentMarqueeItem.heroImageUrl}?image-profile=fetch_showpage_hero` ||
                fallbackUrl
              }
              fallbackUrl={fallbackUrl}
              className={styles.marqueeBackgroundImage}
            />
          )}
        </div>
      ) : null}
      <div
        className={
          styles[`marquee${isLongMarquee ? 'LongTitle' : ''}ContentContainer`]
        }
        id={id}
      >
        {isInbound && currentMarqueeItem ? (
          <MarqueeShowInfo
            watchButtonId={MARQUEE_BUTTON_NAV_ID}
            marqueeItem={currentMarqueeItem}
            onSelect={() => onSelect(marqueeData[currentItemRef.current])}
          />
        ) : null}
        <div className={styles.marqueeItemNavContainer}>
          {marqueeData.map((marqueeItem) => (
            <div
              className={clsx(
                styles.marqueeNavIndicator,
                marqueeItem === currentMarqueeItem
                  ? styles.activeMarqueeNavIndicator
                  : null,
              )}
            />
          ))}
        </div>
      </div>
    </Nav.Vertical>
  );
};
